import { map } from "lodash";
import { Corpora } from "@radarly/models/corpora";
import { CustomField } from "@radarly/models/custom-field";
import { Group } from "@radarly/models/groups";
import { BlockedAuthors, Project } from "@radarly/models/project";
import {
  PublicPage,
  PublicPageCorpus,
  PublicPageCtxFocuse,
  PublicPageCtxRecoImage,
} from "@radarly/models/public-pages";
import { Query } from "@radarly/models/queries";
import { CreateProjectUser } from "@radarly/models/user";
import { ProjectRepo } from "./ProjectRepo";

const currentCreatedDate = new Date();

function fromPublicPageLogoToValidLogo(logo: PublicPageCtxRecoImage) {
  return logo.id;
}

function fromPublicPageCustomFieldToValidCF(customField: unknown): CustomField {
  return customField as CustomField;
}

function fromPublicPageCorpusToValidCorpus(corpus: PublicPageCorpus): Corpora {
  return {
    ...corpus,
    isPublic: false,
    created: currentCreatedDate,
  };
}

function fromPublicPageUserTagsToValidGroup([id, label]: [
  number,
  string
]): Group {
  return {
    id,
    label,
    created: "", // @TODO
  };
}

function fromPublicPageFocusToValidQuery(focus: PublicPageCtxFocuse): Query {
  return {
    ...focus,
    activated: true,
    description: "",
    isSource: false,
    isLocked: false,
    isPending: false,
    imageUrl: "",
    pictureId: 1,
    created: "",
    gnipRate: 0,
    isDynamic: false,
    projectId: 0,
    queryLength: 0,
    updated: "",
    parents: [],
    filter: { clause: null },
    canBeStatic: false,
    shouldBeStatic: false,
  };
}

const FAKE_FACEBOOK_ID = "830394420349622";

function fromPublicPageToProjectCredentials(
  publicPage: PublicPage
): Partial<Project["credentials"]> {
  return {
    facebook:
      publicPage.ctx.hasCredentials.facebook ||
      publicPage.ctx.hasCredentials.instagram
        ? [
            {
              id: FAKE_FACEBOOK_ID,
              error: false,
              created: "",
              gender: null,
              name: "",
              profileImageUrl: "",
              updated: "",
              url: "",
            },
          ]
        : [],
  };
}

function fromPublicPageToProjectSocialAccounts(
  publicPage: PublicPage
): Project["socialAccounts"] {
  return {
    // @ts-ignore TODO: add comment
    facebook: publicPage.ctx.hasCredentials.facebook
      ? [
          {
            id: FAKE_FACEBOOK_ID,
          },
        ]
      : [],
    // @ts-ignore TODO: add comment
    instagram: publicPage.ctx.hasCredentials.instagram
      ? [
          {
            id: FAKE_FACEBOOK_ID,
            relationType: "me",
            credentialIds: [FAKE_FACEBOOK_ID],
          },
        ]
      : [],
  };
}

function getCurrentProject(publicPage: PublicPage) {
  return {
    id: publicPage.customPage.projectId,
    brandLogos: map(publicPage.ctx.brandLogos, fromPublicPageLogoToValidLogo),
    personIdentifications: map(
      publicPage.ctx.personIdentifications,
      fromPublicPageLogoToValidLogo
    ),
    platforms: publicPage.ctx.platforms,
    corpora: map(publicPage.ctx.corpora, fromPublicPageCorpusToValidCorpus),
    focuses: map(publicPage.ctx.focuses, fromPublicPageFocusToValidQuery),
    tags: {
      user: map(publicPage.ctx.userTags, fromPublicPageUserTagsToValidGroup),
      custom: map(
        publicPage.ctx.customFields as CustomField[],
        fromPublicPageCustomFieldToValidCF
      ),
    },
    credentials: fromPublicPageToProjectCredentials(publicPage),
    socialAccounts: fromPublicPageToProjectSocialAccounts(publicPage),
  };
}

export class PublicProjectRepo implements ProjectRepo {
  currentProject: Project;
  constructor(publicPage: PublicPage) {
    this.currentProject = getCurrentProject(publicPage);
  }

  contactProjectManager(
    project: number,
    subject: string,
    content: string
  ): never {
    throw new Error("Implement contactProjectManager");
  }
  getCurrentProject() {
    return this.currentProject;
  }
  getProject(project: number): never {
    throw new Error("Implement getProject");
  }
  generateProjectPictureUrl(project: number, picture: number): never {
    throw new Error("Implement generateProjectPictureUrl");
  }
  getProjectPicturesUsage(project: number): never {
    throw new Error("Implement getProjectPicturesUsage");
  }
  getProjectBlockedAuthors(project: number): Promise<BlockedAuthors> {
    // Edge case not handled : Ignore blocked authors in snapshot
    return {} as any;
  }
  removeUser(project: number, userId: number): never {
    throw new Error("Implement removeUser");
  }
  removeUsers(project: number, usersId: number[]): never {
    throw new Error("Implement removeUsers");
  }
  createProjectUser(project: number, user: CreateProjectUser): never {
    throw new Error("Implement createProjectUser");
  }
  getWeiboStatus(): never {
    throw new Error("Implement getWeiboStatus");
  }
}
